<template>
  <div class="d-flex align-items-center flex-wrap gap-3">
    <div class="d-flex flex-col">
      <b>Time Period</b>
      <div class="flex gap-3">
        <Select
          v-model="period"
          class="mb-0 period-select"
          :options="periods"
          full-width
        />
        <Select
          :key="period"
          v-model="offset"
          class="mb-0"
          :options="offsets"
          :full-width="false"
        />
      </div>
    </div>
    <div class="d-flex flex-col">
      <b>Compare To</b>
      <Select
        v-model="comparisonPeriod"
        class="mb-0"
        :options="comparisonPeriods"
        :full-width="false"
      />
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { mapActions } from "vuex";
import {
  buildCurrentRange,
  buildPreviousRange,
  previousYearRange
} from "@/lib/dates.js";
import { browserTimezone } from "@/lib/helpers.js";
import { calculatePeriodOffsets } from "@/lib/performance_date_filters.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import CONSTANTS from "@/Constants.js";

export default {
  data() {
    return {
      offset: null,
      period: null,
      comparisonPeriod: null,
      periods: [
        {
          value: "week",
          label: "Week"
        },
        {
          value: "30",
          label: "30 Days"
        },
        {
          value: "month",
          label: "Month"
        },
        {
          value: "quarter",
          label: "Quarter"
        },
        {
          value: "year",
          label: "Year"
        }
      ]
    };
  },
  computed: {
    comparisonPeriods() {
      const samePeriod = {
        value: "samePeriod",
        label: `Prior ${this.periodLabel}`
      };
      const periodLastYear = {
        value: "periodLastYear",
        label: `Same ${this.periodLabel} Prior Year`
      };
      if (this.period === "year") {
        return [samePeriod];
      }
      return [samePeriod, periodLastYear];
    },
    periodLabel() {
      if (!this.period || !this.periods.length) {
        return "--";
      }
      return this.periods.find((period) => period.value === this.period).label;
    },
    offsets() {
      return calculatePeriodOffsets(this.period);
    },
    currentOffsetLabel() {
      return this.offsets.find((offset) => offset.value === this.offset).label;
    }
  },
  watch: {
    async period(newValue, oldValue) {
      if (newValue !== oldValue) {
        await updateQueryParam("period", newValue, true);
        // Don't reset offset on intial page load
        if (oldValue) {
          this.offset = null;
        }
        if (newValue === "year") {
          this.comparisonPeriod = "samePeriod";
        }

        // Quick fix to avoid asynchronicity pitfalls when changing
        // period to "30 Days"
        this.$nextTick(() => {
          if (!this.offset && this.offsets.length) {
            this.offset = this.offsets[0].value;
          }
          this.setFilters();
        });
      }
    },
    async offset(newValue, oldValue) {
      if (newValue !== oldValue && this.offset) {
        await updateQueryParam("offset", newValue, true);
        this.setFilters();
      }
    },
    async comparisonPeriod(newValue, oldValue) {
      if (newValue !== oldValue) {
        await updateQueryParam("comparisonPeriod", newValue, true);
        this.setFilters();
      }
    },
    offsets(newValue, oldValue) {
      if (newValue !== oldValue && !this.offset) {
        const defaultOffset = newValue[0]?.value;
        this.offset = defaultOffset;
      }
    }
  },
  mounted() {
    this.initFilters();
    this.setDefaultFilters();
  },
  beforeUnmount() {
    this.setDashboardFilters({
      base_range: "",
      comparison_range: "",
      granularity: "",
      period: "",
      offset: ""
    });
  },
  methods: {
    ...mapActions(["setDashboardFilters"]),
    getComparisonDateRange() {
      if (!this.period || !this.comparisonPeriod) return;
      if (this.comparisonPeriod === "samePeriod") {
        const period = this.period.includes("30") ? "days" : this.period;
        const offset = this.getComparisonOffset();
        const range = buildPreviousRange(period, offset);
        return this.formatDateRange(range);
      }
      const range = this.getBaseDateRange();
      return this.formatDateRange(previousYearRange(range.split("-")));
    },
    getComparisonOffset() {
      // adds 31 day offset to get previous range
      if (this.period.includes("30")) {
        return this.offset.split(",").map((days) => parseInt(days, 10) + 31);
      }
      // if any current(default) option is set then our offset is the minimum value
      if (this.offset === "current") {
        return [1, 1];
      }
      // calculates the offset from the previous period to the current selected
      return this.offset.split(",").map((days) => parseInt(days, 10) + 1);
    },
    getBaseDateRange() {
      if (!this.period) return;
      const isDefaultOffset = this.offset === "current";
      const rangeFn = isDefaultOffset ? buildCurrentRange : buildPreviousRange;
      if (this.period === "30")
        return this.formatDateRange(
          buildPreviousRange("days", this.offset.split(","))
        );

      return this.formatDateRange(rangeFn(this.period, this.offset.split(",")));
    },
    getGranularity() {
      switch (this.period) {
        case "quarter": {
          return "bi_month";
        }
        case "year": {
          return "month";
        }
        default:
          return "day";
      }
    },
    initFilters() {
      const params = getQueryVariables();
      if (params.period) {
        this.period = params.period;
      }
      if (params.offset) {
        this.offset = params.offset;
      }
      if (params.comparisonPeriod) {
        this.comparisonPeriod = params.comparisonPeriod;
      }
    },
    setDefaultFilters() {
      this.period = this.period || "month";
      this.comparisonPeriod = this.comparisonPeriod || "periodLastYear";
    },
    setFilters: debounce(function () {
      const base_range = this.getBaseDateRange();
      const comparison_range = this.getComparisonDateRange();
      const granularity = this.getGranularity();

      this.setDashboardFilters({
        base_range,
        comparison_range,
        granularity,
        period: this.period,
        offset: this.currentOffsetLabel
      });
    }, 500),
    formatDateRange(range) {
      return `${moment(range[0])
        .tz(browserTimezone)
        .format(CONSTANTS.DATE_API_FORMAT)}-${moment(range[1])
        .tz(browserTimezone)
        .format(CONSTANTS.DATE_API_FORMAT)}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.period-select {
  width: 120px;
}
</style>
